'use client'
// L'organisation « branchée » de l'usager : celle que le cockpit a retenue (ab-dash-org), sinon la première de /api/orgs.
// Son identité (logo téléversé, site Web, coordonnées) vient de /api/orgs/:id/identity. Aucune valeur de démonstration :
// sans identité configurée — ou si l'API ne la fournit pas — on garde le repli (logo Amiral + initiales du nom).
import { useEffect, useState } from 'react'

export interface OrgBrand {
  id: string
  name: string
  slug: string | null
  /** URL du logo téléversé (version incluse pour le cache), ou null → repli. */
  logoSrc: string | null
  websiteUrl: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  address: string | null
  canEdit: boolean
}

interface IdentityDto {
  name: string; websiteUrl: string | null; contactName: string | null; contactEmail: string | null; contactPhone: string | null; address: string | null
  hasLogo: boolean; logoVersion: number | null; canEdit: boolean
}

/** Deux lettres au plus : « Les Sports aux Puces » → « LS », « Vortex » → « V ». */
export function orgInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(w => /[\p{L}\p{N}]/u.test(w))
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? '?').slice(0, 1)).toUpperCase()
}

export const ORG_BRAND_EVENT = 'ab-org-identity-changed'

export function useOrgBrand(enabled = true): OrgBrand | null {
  const [org, setOrg] = useState<OrgBrand | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const on = () => setTick(t => t + 1)
    window.addEventListener(ORG_BRAND_EVENT, on)
    return () => window.removeEventListener(ORG_BRAND_EVENT, on)
  }, [])

  useEffect(() => {
    if (!enabled) return
    let off = false
    ;(async () => {
      try {
        const r = await fetch('/api/orgs')
        const d = r.ok ? await r.json() as { organizations?: { id: string; name: string; slug?: string }[] } : { organizations: [] }
        const list = d.organizations ?? []
        let saved = ''
        try { saved = localStorage.getItem('ab-dash-org') ?? '' } catch { /* stockage indisponible */ }
        const o = list.find(x => x.id === saved) ?? list[0]
        if (!o || off) return
        const base: OrgBrand = { id: o.id, name: o.name, slug: o.slug ?? null, logoSrc: null, websiteUrl: null, contactName: null, contactEmail: null, contactPhone: null, address: null, canEdit: false }
        setOrg(base)
        const ir = await fetch(`/api/orgs/${encodeURIComponent(o.id)}/identity`).catch(() => null)
        if (!ir || !ir.ok || off) return   // identité indisponible : on garde le repli
        const i = await ir.json() as IdentityDto
        if (!off) setOrg({ ...base, name: i.name || base.name, websiteUrl: i.websiteUrl, contactName: i.contactName, contactEmail: i.contactEmail, contactPhone: i.contactPhone, address: i.address, canEdit: i.canEdit,
          logoSrc: i.hasLogo ? `/api/org-logo/${encodeURIComponent(o.id)}?v=${i.logoVersion ?? 0}` : null })
      } catch { /* repli */ }
    })()
    return () => { off = true }
  }, [enabled, tick])

  return org
}
